const STORAGE_KEY = "previewloop:island-position";
const SESSION_HIDDEN_KEY = "previewloop:island-hidden";
const VIEWPORT_GUTTER = 12;

export type WidgetIslandPosition = {
  right: number;
  bottom: number;
};

const isPosition = (value: unknown): value is WidgetIslandPosition => {
  if (typeof value !== "object" || value === null) return false;
  if (!("right" in value) || !("bottom" in value)) return false;
  return (
    typeof value.right === "number" &&
    Number.isFinite(value.right) &&
    typeof value.bottom === "number" &&
    Number.isFinite(value.bottom)
  );
};

/** Keep the launcher fully reachable after a drag or viewport resize. */
export function clampWidgetIslandPosition(
  position: WidgetIslandPosition,
  launcherSize: { width: number; height: number },
  viewportSize: { width: number; height: number },
): WidgetIslandPosition {
  return {
    right: Math.min(
      Math.max(position.right, VIEWPORT_GUTTER),
      Math.max(VIEWPORT_GUTTER, viewportSize.width - launcherSize.width - VIEWPORT_GUTTER),
    ),
    bottom: Math.min(
      Math.max(position.bottom, VIEWPORT_GUTTER),
      Math.max(VIEWPORT_GUTTER, viewportSize.height - launcherSize.height - VIEWPORT_GUTTER),
    ),
  };
}

/** Correct an anchored launcher position until its complete visible UI stays onscreen. */
export function nudgeWidgetIslandPositionIntoViewport(
  position: WidgetIslandPosition,
  footprint: { top: number; right: number; bottom: number; left: number },
  viewportSize: { width: number; height: number },
): WidgetIslandPosition {
  let right = position.right;
  let bottom = position.bottom;
  if (footprint.left < VIEWPORT_GUTTER) right -= VIEWPORT_GUTTER - footprint.left;
  if (footprint.right > viewportSize.width - VIEWPORT_GUTTER)
    right += footprint.right - (viewportSize.width - VIEWPORT_GUTTER);
  if (footprint.top < VIEWPORT_GUTTER) bottom -= VIEWPORT_GUTTER - footprint.top;
  if (footprint.bottom > viewportSize.height - VIEWPORT_GUTTER)
    bottom += footprint.bottom - (viewportSize.height - VIEWPORT_GUTTER);
  return { right, bottom };
}

/** Read the origin-local launcher preference. Storage can be unavailable in strict browsers. */
export function loadWidgetIslandPosition(): WidgetIslandPosition | null {
  try {
    const serialized = window.localStorage.getItem(STORAGE_KEY);
    if (!serialized) return null;
    const value: unknown = JSON.parse(serialized);
    return isPosition(value) ? value : null;
  } catch {
    return null;
  }
}

export function saveWidgetIslandPosition(position: WidgetIslandPosition): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch {
    // The drag still works when storage is blocked or full.
  }
}

export function isWidgetIslandHiddenForSession(): boolean {
  try {
    return window.sessionStorage.getItem(SESSION_HIDDEN_KEY) === "true";
  } catch {
    return false;
  }
}

export function hideWidgetIslandForSession(): void {
  try {
    window.sessionStorage.setItem(SESSION_HIDDEN_KEY, "true");
  } catch {
    // Hiding still applies until this page is unloaded.
  }
}

export function showWidgetIslandForSession(): void {
  try {
    window.sessionStorage.removeItem(SESSION_HIDDEN_KEY);
  } catch {
    // The in-memory state still restores the launcher on this page.
  }
}
